import fs from 'node:fs'
import path from 'node:path'
import type { ResolvedBetterstartConfig } from '@cli-types/core-engine/config.js'
import type { BetterstartInstalledManifest } from '@cli-types/core-engine/installable.js'
import type {
  AnyInstallableDefinition,
  InstallableRuntime,
  InstallableRuntimeOptions
} from '@cli-types/next-adapter/runtime.js'
import { betterstartDir, cleanupEmptyDirs, toProjectAbsolutePath } from '@cli-utils/paths.js'
import { normalizeLineEndings } from '@cli-utils/text.js'
import { resolveProjectPaths } from '@next-adapter/config/paths/resolve-project-paths.js'
import { getDependencyPlan } from '@next-adapter/init/scaffolders/dependencies/get-dependency-plan.js'
import { readTemplate } from '@next-adapter/template-reader/read-template.js'
import { namespaceInstallableContent } from './namespace-installable-content.js'
import { resolveInstallableProjectPath } from './resolve-installable-project-path.js'
import { tryReadCoreManagedTemplate } from './try-read-core-managed-template.js'

export function createInstallableRuntime<
  TDefinition extends AnyInstallableDefinition,
  TManifest extends BetterstartInstalledManifest<string>
>({
  familySegment,
  readExtensionTemplate,
  getDefinition,
  getCrossDefinition,
  loadManifest,
  saveManifest
}: InstallableRuntimeOptions<TDefinition, TManifest>): InstallableRuntime<TDefinition> {
  function resolveProjectPath(config: ResolvedBetterstartConfig, outputPath: string): string {
    return resolveInstallableProjectPath(config, outputPath)
  }

  function namespaceContent(config: ResolvedBetterstartConfig, content: string): string {
    return namespaceInstallableContent(config, content)
  }

  return {
    resolveProjectPath,
    namespaceContent,

    ensureDirectFileTargetsAvailable(cwd, config, definition) {
      for (const file of definition.files) {
        const projectRelativePath = resolveProjectPath(config, file.outputPath)
        const fullPath = path.join(cwd, ...projectRelativePath.split('/'))

        if (!fs.existsSync(fullPath)) {
          continue
        }

        const stats = fs.statSync(fullPath)
        if (!stats.isFile()) {
          throw new Error(
            `Installing ${definition.id} would overwrite "${projectRelativePath}", but it is not a file. Remove or rename it before continuing.`
          )
        }

        const existingContent = normalizeLineEndings(fs.readFileSync(fullPath, 'utf-8'))
        const extensionTemplate = normalizeLineEndings(
          namespaceContent(config, readExtensionTemplate(definition.id, file.templatePath))
        )

        if (existingContent === extensionTemplate) {
          continue
        }

        const coreTemplate = tryReadCoreManagedTemplate(file.outputPath)
        if (
          coreTemplate &&
          existingContent === normalizeLineEndings(namespaceContent(config, coreTemplate))
        ) {
          continue
        }

        throw new Error(
          `Installing ${definition.id} would overwrite existing file "${projectRelativePath}". Rename, remove, or reset it to the managed template before installing ${definition.id}.`
        )
      }
    },

    writeDirectFiles(cwd, config, definition) {
      const writtenFiles: string[] = []

      for (const file of definition.files) {
        const projectRelativePath = resolveProjectPath(config, file.outputPath)
        const fullPath = path.join(cwd, ...projectRelativePath.split('/'))
        fs.mkdirSync(path.dirname(fullPath), { recursive: true })
        fs.writeFileSync(
          fullPath,
          namespaceContent(config, readExtensionTemplate(definition.id, file.templatePath)),
          'utf-8'
        )
        writtenFiles.push(projectRelativePath)
      }

      return writtenFiles
    },

    cleanupEmptyDirs(cwd, deletedPaths, config) {
      const paths = resolveProjectPaths(config)
      const projectRoot = path.resolve(cwd)

      cleanupEmptyDirs(cwd, deletedPaths, [
        toProjectAbsolutePath(cwd, paths.adminDir),
        toProjectAbsolutePath(cwd, paths.pagesDir),
        toProjectAbsolutePath(cwd, paths.schemasDir),
        betterstartDir(projectRoot),
        betterstartDir(projectRoot, familySegment),
        betterstartDir(projectRoot, 'snapshots')
      ])
    },

    restoreOrDeleteFile(cwd, config, relativeOutputPath) {
      const projectRelativePath = resolveProjectPath(config, relativeOutputPath)
      const fullPath = toProjectAbsolutePath(cwd, projectRelativePath)

      try {
        const coreTemplate = readTemplate(relativeOutputPath)
        fs.mkdirSync(path.dirname(fullPath), { recursive: true })
        fs.writeFileSync(fullPath, namespaceContent(config, coreTemplate), 'utf-8')
        return null
      } catch {
        const existed = fs.existsSync(fullPath)
        fs.rmSync(fullPath, { force: true })
        return existed ? projectRelativePath : null
      }
    },

    syncInstalledManifests(cwd, config, installedIds) {
      for (const id of installedIds) {
        const manifest = loadManifest(cwd, id)
        if (!manifest) {
          continue
        }

        const definition = getDefinition(id)
        const nextManifest: TManifest = {
          ...manifest,
          version: definition.version,
          kind: definition.kind,
          ownedFiles: definition.files.map((file) => resolveProjectPath(config, file.outputPath)),
          packageDependencies: definition.packageDependencies,
          devDependencies: definition.devDependencies,
          dependencies: definition.dependencies,
          conflicts: definition.conflicts
        }

        if (JSON.stringify(nextManifest) !== JSON.stringify(manifest)) {
          saveManifest(cwd, nextManifest)
        }
      }
    },

    getUnusedDependencies(remainingIds, removedId, crossSetIds) {
      const remainingDefinitions = remainingIds.map((id) => getDefinition(id))
      const crossDefinitions = crossSetIds.map((id) => getCrossDefinition(id))
      const remainingDependencies = new Set([
        ...remainingDefinitions.flatMap((definition) => definition.packageDependencies),
        ...crossDefinitions.flatMap((definition) => definition.packageDependencies)
      ])
      const remainingDevDependencies = new Set([
        ...remainingDefinitions.flatMap((definition) => definition.devDependencies),
        ...crossDefinitions.flatMap((definition) => definition.devDependencies)
      ])

      const corePlan = getDependencyPlan([], [], false)
      const removedDefinition = getDefinition(removedId)

      return {
        dependencies: removedDefinition.packageDependencies.filter(
          (dependency) =>
            !remainingDependencies.has(dependency) && !corePlan.dependencies.includes(dependency)
        ),
        devDependencies: removedDefinition.devDependencies.filter(
          (dependency) =>
            !remainingDevDependencies.has(dependency) &&
            !corePlan.devDependencies.includes(dependency)
        )
      }
    }
  }
}
